'use client';
import { Box, Typography, Button, Card, CardContent, Container, TextField, Grid, Paper, Chip, LinearProgress } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import { Shield, AccessTime, Star, TrendingUp, Speed, CheckCircle, Psychology, Business, Assessment, Settings, Security, Analytics, Warning, Timeline, Group, VerifiedUser } from '@mui/icons-material';
import TopNavigation from '@/components/TopNavigation';
import Footer from '@/components/Footer';

export default function Home() {
  const keyFeatures = [
    {
      icon: Assessment,
      title: "QAPI Management",
      description: "Transform your quality assurance with AI-powered insights and automated compliance tracking",
      benefits: ["91% reduction in manual audits", "Real-time F-tag monitoring", "Automated citation prevention"],
      color: "#153C7F",
      stat: "91%"
    },
    {
      icon: Settings,
      title: "Workflow Automation",
      description: "Streamline operations with intelligent process automation that learns from your team",
      benefits: ["12x faster referral processing", "Smart form completion", "Automated task routing"],
      color: "#00B4D8",
      stat: "12x"
    },
    {
      icon: Security,
      title: "Compliance Management",
      description: "Stay ahead of regulatory changes with predictive compliance and automated deadline management",
      benefits: ["95% compliance improvement", "Predictive F-tag alerts", "State regulation tracking"],
      color: "#28A745",
      stat: "95%"
    },
    {
      icon: Analytics,
      title: "Advanced Analytics",
      description: "Data-driven insights that transform your facility's performance and outcomes",
      benefits: ["Real-time dashboards", "Predictive analytics", "Performance benchmarking"],
      color: "#6F42C1",
      stat: "30%"
    }
  ];

  const customerStories = [
    {
      quote: "CareAscendium transformed our QAPI process. We went from spending 15 hours per week on manual audits to just 1 hour. Our CMS star rating improved from 3 to 4.5 stars in just 6 months.",
      author: "Sarah Johnson, RN",
      role: "Director of Nursing",
      facility: "Sunrise Care Center",
      outcomes: ["15hrs → 1hr audit time", "3★ → 4.5★ CMS rating", "Zero citations in 8 months"]
    },
    {
      quote: "The automation features are incredible. We process referrals 12 times faster, and our admission decisions are more accurate than ever. It's like having an extra team member working 24/7.",
      author: "Michael Chen",
      role: "Admissions Director",
      facility: "Golden Years Facility",
      outcomes: ["12x faster referrals", "95% accuracy improvement", "24/7 automated support"]
    },
    {
      quote: "Our compliance team went from reactive to proactive. We're now preventing issues before they happen, and our regulatory scores have never been higher.",
      author: "Dr. Emily Rodriguez",
      role: "Quality Assurance Manager",
      facility: "Heritage Nursing Home",
      outcomes: ["Proactive compliance", "95% regulatory score", "Zero survey deficiencies"]
    }
  ];

  const stats = [
    {
      icon: Shield,
      title: "Reduce Falls by up to 30%",
      description: "AI-powered risk assessment and prevention protocols",
      value: "30%",
      color: "#153C7F",
      detail: "Through predictive analytics and automated safety monitoring"
    },
    {
      icon: AccessTime,
      title: "Save 10+ Hours/Month",
      description: "Automated workflows and intelligent form processing",
      value: "10+",
      color: "#00B4D8",
      detail: "Eliminating manual data entry and repetitive tasks"
    },
    {
      icon: Star,
      title: "Boost CMS Star Ratings",
      description: "Data-driven insights for continuous improvement",
      value: "4.5★",
      color: "#28A745",
      detail: "Average improvement from 3.2 to 4.5 stars"
    },
    {
      icon: TrendingUp,
      title: "Improve Compliance by 95%",
      description: "Automated tracking and predictive regulatory alerts",
      value: "95%",
      color: "#6F42C1",
      detail: "Preventing citations before they occur"
    }
  ];

  const challenges = [
    {
      icon: Warning,
      title: "Manual Processes",
      description: "Staff spending hours on paperwork instead of patient care",
      impact: "15+ hours/week lost to administrative tasks"
    },
    {
      icon: Timeline,
      title: "Compliance Risks",
      description: "Missing deadlines and regulatory requirements",
      impact: "Average $50K in citations per facility annually"
    },
    {
      icon: Group,
      title: "Staff Burnout",
      description: "Overwhelmed teams struggling with documentation",
      impact: "40% higher turnover in facilities without automation"
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      {/* Top Navigation Bar */}
      <TopNavigation />

      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #153C7F 0%, #1E4A8C 50%, #2A5A9C 100%)',
        py: 12, 
        px: 4,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant="h1" 
            sx={{ 
              color: 'white', 
              mb: 4,
              fontWeight: 800,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            The AI Revolution in Skilled Nursing is Here
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 6,
              color: 'rgba(255,255,255,0.9)',
              maxWidth: '900px',
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}
          >
            <strong>Manual processes are the new compliance risk.</strong> CareAscendium transforms skilled nursing operations with AI-powered automation that prevents citations, saves time, and improves outcomes.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, justifyContent: 'center', alignItems: 'center' }}>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button 
                variant="contained" 
                size="large"
                startIcon={<TrendingUp />}
                sx={{ 
                  bgcolor: 'white',
                  color: '#153C7F',
                  px: 6,
                  py: 3,
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: '0 4px 15px rgba(255,255,255,0.3)',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                    boxShadow: '0 6px 20px rgba(255,255,255,0.4)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Start Free Trial
              </Button>
            </Link>
            <Link href="/about" style={{ textDecoration: 'none' }}>
              <Button 
                variant="outlined" 
                size="large"
                sx={{ 
                  borderColor: 'white',
                  color: 'white',
                  px: 6,
                  py: 3,
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: 'none',
                  borderWidth: 2,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                See How It Works
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>

      {/* The Challenge Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
            The Reality of Modern Skilled Nursing
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
            While technology advances in other healthcare sectors, skilled nursing facilities are still struggling with manual processes that put compliance and quality at risk.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {challenges.map((challenge, index) => (
            <Grid size={{ xs: 12, md: 4 }} key={index}>
              <Paper 
                elevation={6}
                sx={{ 
                  p: 4,
                  height: '100%',
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '1px solid rgba(220,53,69,0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(220,53,69,0.15)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #dc3545, #e74c3c)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 3,
                    boxShadow: '0 4px 12px rgba(220,53,69,0.4)'
                  }}>
                    <challenge.icon sx={{ fontSize: 28, color: 'white' }} />
                  </Box>
                  <Typography variant="h5" sx={{ color: '#dc3545', fontWeight: 700 }}>
                    {challenge.title}
                  </Typography>
                </Box>
                
                <Typography variant="body1" sx={{ color: '#666', mb: 3, lineHeight: 1.6, fontSize: '1rem' }}>
                  {challenge.description}
                </Typography>
                
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'rgba(220,53,69,0.1)', 
                  borderRadius: 2,
                  border: '1px solid rgba(220,53,69,0.2)'
                }}>
                  <Typography variant="body2" sx={{ color: '#dc3545', fontWeight: 600, textAlign: 'center' }}>
                    {challenge.impact}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Quick Stats Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
        py: 10 
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
              The CareAscendium Difference
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
              Real results from real facilities. See how AI-powered automation transforms operations.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Paper 
                  elevation={8}
                  sx={{ 
                    textAlign: 'center', 
                    p: 4,
                    height: '100%',
                    borderRadius: 3,
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                    border: `1px solid ${stat.color}20`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 32px ${stat.color}30`
                    }
                  }}
                >
                  <Box sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: `linear-gradient(45deg, ${stat.color}, ${stat.color}80)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: `0 4px 15px ${stat.color}40`
                  }}>
                    <stat.icon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  
                  <Typography variant="h3" sx={{ color: stat.color, mb: 2, fontWeight: 700 }}>
                    {stat.value}
                  </Typography>
                  
                  <Typography variant="h5" sx={{ color: '#153C7F', mb: 3, fontWeight: 600, fontSize: '1.1rem' }}>
                    {stat.title}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ color: '#666', mb: 2, lineHeight: 1.6 }}>
                    {stat.description}
                  </Typography>

                  <Typography variant="body2" sx={{ color: stat.color, fontWeight: 600, fontSize: '0.9rem' }}>
                    {stat.detail}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Key Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
            One Platform. Complete Transformation.
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
            Everything you need to modernize your skilled nursing facility operations
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {keyFeatures.map((feature, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Paper 
                elevation={6}
                sx={{ 
                  p: 4,
                  height: '100%',
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  border: `1px solid ${feature.color}20`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 25px ${feature.color}20`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: `linear-gradient(45deg, ${feature.color}, ${feature.color}80)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 3,
                    boxShadow: `0 4px 12px ${feature.color}40`
                  }}>
                    <feature.icon sx={{ fontSize: 28, color: 'white' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ color: feature.color, fontWeight: 700, mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="h4" sx={{ color: feature.color, fontWeight: 800, fontSize: '1.8rem' }}>
                      {feature.stat}
                    </Typography>
                  </Box>
                </Box>
                
                <Typography variant="body1" sx={{ color: '#666', mb: 3, lineHeight: 1.6, fontSize: '1rem' }}>
                  {feature.description}
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {feature.benefits.map((benefit, idx) => (
                    <Chip 
                      key={idx}
                      label={benefit} 
                      size="small" 
                      sx={{ 
                        bgcolor: `${feature.color}15`, 
                        color: feature.color,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        border: `1px solid ${feature.color}30`
                      }} 
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Customer Stories Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
        py: 10 
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
              Real Stories, Real Results
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
              Hear from facility leaders who have transformed their operations with CareAscendium
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {customerStories.map((story, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={index}>
                <Paper 
                  elevation={6}
                  sx={{ 
                    p: 4,
                    height: '100%',
                    borderRadius: 3,
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                    border: '1px solid rgba(21,60,127,0.1)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(21,60,127,0.15)'
                    }
                  }}
                >
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ color: '#153C7F', mb: 2, fontWeight: 600, fontSize: '1.3rem', lineHeight: 1.4 }}>
                      "{story.quote}"
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ color: '#153C7F', fontWeight: 700, mb: 1 }}>
                      {story.author}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontWeight: 500, mb: 1 }}>
                      {story.role}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#153C7F', fontWeight: 600, fontSize: '0.9rem' }}>
                      {story.facility}
                    </Typography>
                  </Box>

                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'rgba(21,60,127,0.05)', 
                    borderRadius: 2,
                    border: '1px solid rgba(21,60,127,0.1)'
                  }}>
                    <Typography variant="body2" sx={{ color: '#153C7F', fontWeight: 700, mb: 2, textAlign: 'center' }}>
                      Key Outcomes:
                    </Typography>
                    {story.outcomes.map((outcome, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircle sx={{ fontSize: 16, color: '#28A745', mr: 1 }} />
                        <Typography variant="body2" sx={{ color: '#333', fontSize: '0.85rem' }}>
                          {outcome}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Footer CTA Section */}
      <Box sx={{ 
        py: 12, 
        background: 'linear-gradient(135deg, #153C7F 0%, #1E4A8C 100%)',
        color: 'white',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ mb: 4, fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' } }}>
            Join the AI Revolution
          </Typography>
          <Typography variant="h5" sx={{ mb: 6, opacity: 0.9, lineHeight: 1.6 }}>
            Transform your facility operations with AI-powered automation
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 3,
            justifyContent: 'center',
            alignItems: 'center',
            maxWidth: '600px',
            mx: 'auto'
          }}>
            <TextField
              placeholder="Enter your email address"
              variant="outlined"
              sx={{
                flex: 1,
                minWidth: { xs: '100%', sm: '350px' },
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'white',
                  borderRadius: 3,
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'white',
                  }
                },
                '& .MuiInputBase-input': {
                  color: '#333',
                  fontSize: '1.1rem',
                  py: 2
                }
              }}
            />
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button 
                variant="contained" 
                size="large"
                startIcon={<VerifiedUser />}
                sx={{ 
                  bgcolor: 'white',
                  color: '#153C7F',
                  px: 6,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: '0 4px 15px rgba(255,255,255,0.3)',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                    boxShadow: '0 6px 20px rgba(255,255,255,0.4)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Start Free Trial
              </Button>
            </Link>
          </Box>
          
          <Typography variant="body2" sx={{ mt: 4, opacity: 0.8 }}>
            No credit card required. 15-day free trial with full access.
          </Typography>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
}
